import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MarketingFormsService } from '../../service/marketing-forms.service';
import { FormFieldDropoffModel, FormStatsDailyModel } from '../../models/form-metrics.model';
import { FormListModel } from '../../models/form.model';
import { FormFieldModel } from '../../models/form-field.model';

interface DropoffRow {
  fieldId: number;
  fieldLabel: string;
  views: number;
  interactions: number;
  dropoffs: number;
  dropoffRatePct: number;
}

type RangePreset = '7' | '30' | '90' | 'all' | 'custom';

@Component({
  selector: 'app-analytics-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './analytics-dashboard.component.html',
})
export class AnalyticsDashboardComponent implements OnChanges, OnDestroy {
  @Input() forms: FormListModel[] = [];

  private readonly destroy$ = new Subject<void>();

  selectedFormId: number | null = null;
  loading = false;
  refreshing = false;

  // Datos completos cargados desde el server (descendente por fecha).
  private allStats: FormStatsDailyModel[] = [];
  private allDropoffs: FormFieldDropoffModel[] = [];

  // Datos filtrados por el rango seleccionado.
  stats: FormStatsDailyModel[] = [];
  dropoffs: FormFieldDropoffModel[] = [];
  fieldsById = new Map<number, FormFieldModel>();

  // Filtro de rango (cliente).
  rangePreset: RangePreset = '30';
  customFrom = '';
  customTo = '';

  readonly rangePresets: { value: RangePreset; label: string }[] = [
    { value: '7', label: 'Últimos 7 días' },
    { value: '30', label: 'Últimos 30 días' },
    { value: '90', label: 'Últimos 90 días' },
    { value: 'all', label: 'Todo' },
    { value: 'custom', label: 'Personalizado' },
  ];

  constructor(private formsService: MarketingFormsService) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['forms'] && this.forms.length && this.selectedFormId == null) {
      this.selectedFormId = this.forms[0].id;
      this.load();
    }
  }

  onFormChange(): void {
    this.load();
  }

  // ── Filtros ──────────────────────────────────────────────────────────────

  setRange(preset: RangePreset): void {
    this.rangePreset = preset;
    if (preset !== 'custom') this.applyRange();
  }

  onCustomRangeChange(): void {
    if (this.rangePreset === 'custom' && this.customFrom && this.customTo) {
      this.applyRange();
    }
  }

  private applyRange(): void {
    const [from, to] = this.computeRangeBounds();
    this.stats = this.filterStats(this.allStats, from, to);
    this.dropoffs = this.filterDropoffs(this.allDropoffs, from, to);
  }

  private computeRangeBounds(): [string | null, string | null] {
    if (this.rangePreset === 'all') return [null, null];
    if (this.rangePreset === 'custom') {
      return [this.customFrom || null, this.customTo || null];
    }
    const days = Number(this.rangePreset);
    const today = new Date();
    const fromDate = new Date(today);
    fromDate.setDate(fromDate.getDate() - days + 1);
    return [this.isoDate(fromDate), this.isoDate(today)];
  }

  private filterStats(rows: FormStatsDailyModel[], from: string | null, to: string | null): FormStatsDailyModel[] {
    return rows.filter(r => this.inRange(r.statDate, from, to));
  }

  private filterDropoffs(rows: FormFieldDropoffModel[], from: string | null, to: string | null): FormFieldDropoffModel[] {
    return rows.filter(r => this.inRange(r.statDate, from, to));
  }

  private inRange(date: string, from: string | null, to: string | null): boolean {
    if (from && date < from) return false;
    if (to && date > to) return false;
    return true;
  }

  private isoDate(d: Date): string {
    return d.toISOString().slice(0, 10);
  }

  // ── Datos derivados ──────────────────────────────────────────────────────

  get latestStats(): FormStatsDailyModel | null {
    return this.stats[0] ?? null;
  }

  get totals() {
    return this.stats.reduce(
      (acc, s) => {
        acc.totalViews += s.totalViews;
        acc.totalSubmissions += s.totalSubmissions;
        acc.totalNewContacts += s.totalNewContacts;
        acc.totalPartial += s.totalPartial;
        acc.totalFailed += s.totalFailed;
        return acc;
      },
      { totalViews: 0, totalSubmissions: 0, totalNewContacts: 0, totalPartial: 0, totalFailed: 0 },
    );
  }

  get avgConversion(): number {
    const t = this.totals;
    if (!t.totalViews) return 0;
    return (t.totalSubmissions * 100) / t.totalViews;
  }

  get trafficBreakdown() {
    const acc = { organic: 0, email: 0, social: 0, paid: 0, referral: 0, direct: 0 };
    this.stats.forEach(s => {
      acc.organic += s.viewsOrganic;
      acc.email += s.viewsEmail;
      acc.social += s.viewsSocial;
      acc.paid += s.viewsPaid;
      acc.referral += s.viewsReferral;
      acc.direct += s.viewsDirect;
    });
    const total = this.totals.totalViews || 1;
    return Object.entries(acc).map(([key, v]) => ({
      key,
      label: this.trafficLabel(key),
      value: v,
      pct: (v * 100) / total,
    }));
  }

  // Top fields por dropoff agregado (suma de dropoffs en el rango)
  get dropoffByField(): DropoffRow[] {
    if (!this.dropoffs.length) return [];
    const acc = new Map<number, { views: number; interactions: number; dropoffs: number }>();
    this.dropoffs.forEach(d => {
      const cur = acc.get(d.formFieldId) ?? { views: 0, interactions: 0, dropoffs: 0 };
      cur.views += d.views;
      cur.interactions += d.interactions;
      cur.dropoffs += d.dropoffs;
      acc.set(d.formFieldId, cur);
    });
    return [...acc.entries()].map(([fieldId, v]) => ({
      fieldId,
      fieldLabel: this.fieldsById.get(fieldId)?.label ?? `Campo #${fieldId}`,
      views: v.views,
      interactions: v.interactions,
      dropoffs: v.dropoffs,
      dropoffRatePct: v.interactions ? (v.dropoffs * 100) / v.interactions : 0,
    })).sort((a, b) => b.dropoffRatePct - a.dropoffRatePct);
  }

  // Sparkline en SVG para la curva de conversión (sin libs externas)
  get conversionPath(): { path: string; max: number } {
    if (!this.stats.length) return { path: '', max: 0 };
    const ordered = [...this.stats].sort((a, b) => a.statDate.localeCompare(b.statDate));
    const max = Math.max(...ordered.map(s => s.conversionRatePct), 1);
    const w = 480;
    const h = 120;
    const step = ordered.length > 1 ? w / (ordered.length - 1) : 0;
    const path = ordered.map((s, i) => {
      const x = (i * step).toFixed(1);
      const y = (h - (s.conversionRatePct / max) * h).toFixed(1);
      return `${i === 0 ? 'M' : 'L'}${x},${y}`;
    }).join(' ');
    return { path, max };
  }

  // ── Refresh manual ───────────────────────────────────────────────────────

  refreshToday(): void {
    if (!this.selectedFormId || this.refreshing) return;
    this.refreshing = true;
    this.formsService.refreshStatsToday(this.selectedFormId).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.load(),
      error: () => { this.refreshing = false; },
    });
  }

  // ── Cargar ────────────────────────────────────────────────────────────────

  private load(): void {
    if (!this.selectedFormId) return;
    this.loading = true;
    forkJoin({
      stats: this.formsService.getStatsDaily(this.selectedFormId),
      dropoffs: this.formsService.getFieldDropoffs(this.selectedFormId),
      fields: this.formsService.getFields(this.selectedFormId),
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ stats, dropoffs, fields }) => {
        this.allStats = stats ?? [];
        this.allDropoffs = dropoffs ?? [];
        this.fieldsById = new Map((fields ?? []).filter(f => f.id).map(f => [f.id!, f]));
        this.applyRange();
        this.loading = false;
        this.refreshing = false;
      },
      error: () => {
        this.allStats = [];
        this.allDropoffs = [];
        this.stats = [];
        this.dropoffs = [];
        this.loading = false;
        this.refreshing = false;
      },
    });
  }

  private trafficLabel(key: string): string {
    const m: Record<string, string> = {
      organic: 'Orgánico',
      email: 'Email',
      social: 'Redes sociales',
      paid: 'Pagado',
      referral: 'Referencia',
      direct: 'Directo',
    };
    return m[key] ?? key;
  }
}
